import { Link } from '@anticatrade/shared';
import './Header.css';
import React, { ReactNode, useContext } from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import logo from '../../../assets/logo-simple.svg';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlDropdown from '@shoelace-style/shoelace/dist/react/dropdown/index.js';
import SlMenu from '@shoelace-style/shoelace/dist/react/menu/index.js';
import SlMenuItem from '@shoelace-style/shoelace/dist/react/menu-item/index.js';
import SlAvatar from '@shoelace-style/shoelace/dist/react/avatar/index.js';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const Header = () => {
	const { logout, merchant, unreadMessagesCount, user } = useContext(
		appContext as React.Context<AppContext>,
	);
	const { t } = useTranslation();

	const onLogout = async (e: any) => {
		e.stopPropagation();
		await logout();
	};

	const isSuspended = merchant?.status === 'suspended';

	return (
		<>
			<div className='header'>
				<div className='header__wrapper'>
					<Link className='header__logo-wrapper' path='products'>
						<div className='header__logo'>
							<img className='header__logo-image' src={logo} />
						</div>
					</Link>
					<div className='header__routes'>
						<HeaderRoute route='/products' isDisabled={isSuspended}>
							<SlIcon name='boxes' />
							<span className='header__route-link'>{t('Catalog')}</span>
						</HeaderRoute>
						<HeaderRoute route='/conversations' isDisabled={isSuspended}>
							<SlIcon name='chat-right-text' />
							<span className='header__route-link'>{t('Messages')}</span>
							{unreadMessagesCount != null && unreadMessagesCount > 0 && (
								<span className='header__route-count'>{unreadMessagesCount}</span>
							)}
						</HeaderRoute>
						<HeaderRoute route='/statistics' isDisabled={isSuspended}>
							<SlIcon name='bar-chart-line' />
							<span className='header__route-link'>{t('Statistics')}</span>
						</HeaderRoute>
					</div>
					<div className='header__navigation'>
						<div className='header__account'>
							<SlDropdown
								className='header__account-dropdown'
								placement='bottom-end'
								distance={10}
							>
								<div className='header__account-dropdown-button' slot='trigger'>
									<SlAvatar
										image={
											merchant?.logo
												? `${import.meta.env.VITE_MERCHANT_IMAGE_URL}/${
														merchant?.id
													}/${merchant?.logo.fileName}.webp`
												: undefined
										}
									/>
									<span className='header__account-dropdown-name'>
										{merchant?.name}
									</span>
									<SlIcon
										className='header__account-dropdown-chevron'
										name='chevron-down'
									/>
								</div>
								<SlMenu>
									<div className='header__account-dropdown-head'>
										<div className='header__account-dropdown-head-text'>
											<div className='header__account-dropdown-greeting'>
												<span>{t('Hi')},</span> {user?.firstName}!
											</div>
											<div className='header__account-dropdown-legal'>
												{merchant?.name}
											</div>
											<div className='header__account-dropdown-subscription'>
												{t('Annual plan of {count} objects').replace(
													'{count}',
													String(merchant!.subscriptionLimit),
												)}
											</div>
										</div>
									</div>
									<Link path='merchant'>
										<SlMenuItem>
											<SlIcon name='person-vcard' slot='prefix' />
											<div>{t('Profile')}</div>
										</SlMenuItem>
									</Link>
									<Link path='billing'>
										<SlMenuItem>
											<SlIcon name='credit-card' slot='prefix' />
											<div>{t('Billing')}</div>
										</SlMenuItem>
									</Link>
									<Link path='settings'>
										<SlMenuItem>
											<SlIcon name='gear' slot='prefix' />
											<div>{t('Settings')}</div>
										</SlMenuItem>
									</Link>
									{merchant?.features?.includes('integrations') && (
										<Link path='integrations'>
											<SlMenuItem>
												<SlIcon name='plug' slot='prefix' />
												<div>{t('Integrations')}</div>
											</SlMenuItem>
										</Link>
									)}
									<SlMenuItem onClick={onLogout}>
										<SlIcon name='box-arrow-left' slot='prefix' />
										<div>{t('Logout')}</div>
									</SlMenuItem>
								</SlMenu>
							</SlDropdown>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

interface HeaderRouteProps {
	route: string;
	className?: string;
	children: ReactNode;
	isDisabled?: boolean;
}

const HeaderRoute = ({ route, className, children, isDisabled }: HeaderRouteProps) => {
	const location = useLocation();
	const isCurrent = location.pathname.substring(1).startsWith(route.substring(1));

	return (
		<Link path={isDisabled ? null : route}>
			<div
				className={`header__route${isCurrent && !isDisabled ? ' header__route--selected' : ''}${isDisabled ? ' header__route--disabled' : ''}${
					className != null ? ' ' + className : ''
				}`}
			>
				{children}
			</div>
		</Link>
	);
};

export { Header };
